import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import axios, { AxiosError } from "axios";
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, DatePicker, Form, Input, Modal, Popover, Radio, Row, Space, Spin, Tag } from "antd";
import classnames from "classnames";
import { axiosErrorHandler, debounce, formatMetres, isConcurrentModificationError, isPaymentRequiredError } from "../../../../swzutils";
import { CurrencyFormat } from "../../components/FormattersTS";
import { formatSecondsAsDuration } from '../../../../swzutils/DateUtil';
import MarketplaceConsignmentPaymentMethod from "../../enums/MarketplaceConsignmentPaymentMethod";
import VehicleTypeImgPicker from "../vehicleTypes/VehicleTypeImgPicker";
import HelpLabel from "../../components/HelpLabel";
import { UnitOfMeasureEnum } from "../../api/Representation";
import { DeliveryExpediencyEnum } from "../vehicleRates/api/Representation";
import StripePaymentIntentForm, {} from "../billing/StripePaymentIntentForm";
import { PARAM_SHOW_PLACE_ON_MARKET_MODAL } from "./view/ConsignmentViewPage";
import CurrencyInput from "../../components/CurrencyInput";
import ConsignmentHasBeenInvoicedAlert from "./ConsignmentHasBeenInvoicedAlert";
export var CalculateMode;
(function (CalculateMode) {
    CalculateMode[CalculateMode["POST_IN_MARKET"] = 0] = "POST_IN_MARKET";
    CalculateMode[CalculateMode["QUOTE"] = 1] = "QUOTE";
})(CalculateMode || (CalculateMode = {}));
const CalculateAmountForm = ({ consignment, marketplaceListing, mode, onQuote }, exportedRef) => {
    const [form] = Form.useForm();
    const [vehicleTypeId, setVehicleTypeId] = useState(undefined);
    const [deliveryExpediencyCode, setDeliveryExpediencyCode] = useState();
    const [offerCode, setOfferCode] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [hasTonnes, setHasTonnes] = useState(false);
    const [payAsReturn, setPayAsReturn] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(MarketplaceConsignmentPaymentMethod.DELIVERY);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [serviceAndProductCostToTenant, setServiceAndProductCostToTenant] = useState(undefined);
    const [calculatedTonnageRate, setCalculatedTonnageRate] = useState(undefined);
    const [totalCost, setTotalCost] = useState(undefined);
    const [fuelLevyAmount, setFuelLevyAmount] = useState(undefined);
    const [quote, setQuote] = useState(undefined);
    useImperativeHandle(exportedRef, () => ({
        resetFields: () => {
            form.resetFields();
        },
        validateFields: () => {
            return form.validateFields();
        }
    }));
    useEffect(() => {
        const paymentMethod = checkConsignmentHasTonnes();
        if (marketplaceListing) {
            onMarketplaceListingChanged();
        }
        else {
            form.setFieldsValue({
                deliveryExpediency: deliveryExpediencyCode,
                nominatedCost: null,
                nominatedTonnageRate: null
            });
            loadQuote(undefined, undefined, paymentMethod, undefined, initialPickupReadyAt(), false);
        }
    }, [consignment, marketplaceListing]);
    const checkConsignmentHasTonnes = () => {
        const hasTonnes = consignment.freight?.length ? consignment.freight.filter(f => f.unitOfMeasure === UnitOfMeasureEnum.WEIGHT_TON).length > 0 : false;
        const paymentMethod = hasTonnes
            ? MarketplaceConsignmentPaymentMethod.TONNE
            : MarketplaceConsignmentPaymentMethod.DELIVERY;
        setHasTonnes(hasTonnes);
        setPaymentMethod(paymentMethod);
        return paymentMethod;
    };
    const onMarketplaceListingChanged = () => {
        if (marketplaceListing) {
            const paymentMethod = MarketplaceConsignmentPaymentMethod.valueOf(marketplaceListing.paymentMethod) || MarketplaceConsignmentPaymentMethod.DELIVERY;
            const vehicleTypeId = marketplaceListing.preferredVehicleType.id;
            const deliveryExpediencyCode = marketplaceListing.deliveryExpediency.code;
            const payAsReturn = marketplaceListing.payAsReturn;
            const offerCode = (marketplaceListing.offer && marketplaceListing.offer.code) ? marketplaceListing.offer.code : undefined;
            setPaymentMethod(paymentMethod);
            setVehicleTypeId(vehicleTypeId);
            setDeliveryExpediencyCode(deliveryExpediencyCode);
            setOfferCode(offerCode);
            setPayAsReturn(payAsReturn);
            setTotalCost(marketplaceListing.totalCostToTenant);
            setFuelLevyAmount(marketplaceListing.fuelLevyAmount);
            setServiceAndProductCostToTenant(marketplaceListing.serviceAndProductNominatedCostByTenant);
            setCalculatedTonnageRate(marketplaceListing.calculatedTonnageRate);
            form.setFieldsValue({
                paymentMethod: paymentMethod,
                nominatedCost: marketplaceListing.serviceAndProductNominatedCostByTenant,
                nominatedTonnageRate: marketplaceListing.nominatedTonnageRate,
                deliveryExpediency: deliveryExpediencyCode,
                offerCode: offerCode,
                payAsReturn: payAsReturn
            });
            loadQuote(vehicleTypeId, undefined, paymentMethod, offerCode, undefined, payAsReturn);
        }
    };
    const onPaymentMethodChanged = (v) => {
        const paymentMethod = MarketplaceConsignmentPaymentMethod.valueOf(v.target.value) || MarketplaceConsignmentPaymentMethod.DELIVERY;
        setPaymentMethod(paymentMethod);
        const pickupReadyAt = form.getFieldValue("pickupReadyAt");
        loadQuote(vehicleTypeId, deliveryExpediencyCode, paymentMethod, offerCode, pickupReadyAt, payAsReturn);
    };
    const onValidateOrRemoveDiscountCode = (discountCode) => {
        let newDiscountCode = discountCode;
        if (checkHasAnyCustomerDiscountAmount()) {
            newDiscountCode = '';
            form.setFieldsValue({
                offerCode: newDiscountCode
            });
        }
        setOfferCode(newDiscountCode);
        const pickupReadyAt = form.getFieldValue("pickupReadyAt");
        loadQuote(vehicleTypeId, deliveryExpediencyCode, paymentMethod, newDiscountCode, pickupReadyAt, payAsReturn);
    };
    const onPayAsReturnChanged = (v) => {
        const payAsReturn = v.target.checked;
        setPayAsReturn(payAsReturn);
        const pickupReadyAt = form.getFieldValue("pickupReadyAt");
        loadQuote(vehicleTypeId, deliveryExpediencyCode, paymentMethod, offerCode, pickupReadyAt, payAsReturn);
    };
    const refreshQuote = () => {
        const pickupReadyAt = form.getFieldValue("pickupReadyAt");
        loadQuote(vehicleTypeId, deliveryExpediencyCode, paymentMethod, offerCode, pickupReadyAt, payAsReturn);
    };
    const debouncedRefreshQuote = debounce(refreshQuote, 1500);
    const loadQuote = (vehicleTypeId, selectedDeliveryExpediencyCode, paymentMethod, offerCode, pickupReadyAt, payAsReturn) => {
        setLoading(true);
        setErrorMessage(undefined);
        const nominatedCost = selectedDeliveryExpediencyCode ? form.getFieldValue("nominatedCost") : undefined;
        const nominatedTonnageRate = selectedDeliveryExpediencyCode ? form.getFieldValue("nominatedTonnageRate") : undefined;
        const queryParams = {
            offerCode,
            nominatedCost,
            nominatedTonnageRate,
            paymentMethod: paymentMethod.key,
            preferredVehicleTypeId: vehicleTypeId,
            expediency: selectedDeliveryExpediencyCode,
            pickupReadyAt: pickupReadyAt == undefined ? undefined : pickupReadyAt.toISOString(),
            payAsReturn: payAsReturn
        };
        let quoteRequest;
        if (mode == CalculateMode.QUOTE) {
            quoteRequest = axios("/admin/v1/marketplace/quote", {
                method: "put",
                params: queryParams,
                data: consignment
            });
        }
        else {
            const consignmentId = consignment.id;
            quoteRequest = axios.get(`/admin/v1/consignments/${consignmentId}/marketplace/quote`, {
                params: {
                    ...queryParams
                }
            });
        }
        quoteRequest.then(response => {
            const quoteResponse = response.data;
            form.setFieldsValue({
                "paymentMethod": quoteResponse.paymentMethod
            });
            if (selectedDeliveryExpediencyCode) {
                const selectedExpediency = quoteResponse.deliveryExpediencies.find(quote => quote.deliveryExpediency.code === selectedDeliveryExpediencyCode);
                if (selectedExpediency) {
                    setServiceAndProductCostToTenant(selectedExpediency.serviceAndProductCostToTenant);
                    setCalculatedTonnageRate(selectedExpediency.calculatedTonnageRate);
                    setFuelLevyAmount(selectedExpediency.fuelLevyAmount);
                    setTotalCost(selectedExpediency.totalCost);
                    if (quote) {
                        const updatedQuote = {
                            paymentMethod: quoteResponse.paymentMethod,
                            nominatedCostByTenant: quoteResponse.nominatedCostByTenant,
                            nominatedTonnageRate: quoteResponse.nominatedTonnageRate,
                            effectiveTonnes: quoteResponse.effectiveTonnes,
                            marketplaceDistanceMetres: quoteResponse.marketplaceDistanceMetres,
                            marketplaceTravelSeconds: quoteResponse.marketplaceTravelSeconds,
                            preferredVehicleType: quoteResponse.preferredVehicleType,
                            fuelLevyNominatedPercent: quoteResponse.fuelLevyNominatedPercent,
                            payAsReturn: quoteResponse.payAsReturn,
                            // The quote response only includes the selected deliveryExpediency so we need to merge it in to the others in the state
                            deliveryExpediencies: quote.deliveryExpediencies.map(expediency => {
                                if (selectedDeliveryExpediencyCode === expediency.deliveryExpediency.code) {
                                    return selectedExpediency;
                                }
                                return expediency;
                            })
                        };
                        setQuote(updatedQuote);
                    }
                    else {
                        setQuote(quoteResponse);
                    }
                }
            }
            else {
                const selectedExpediency = quoteResponse.deliveryExpediencies.find(quote => quote.deliveryExpediency.code === deliveryExpediencyCode);
                if (selectedExpediency) {
                    setServiceAndProductCostToTenant(selectedExpediency.serviceAndProductCostToTenant);
                    setCalculatedTonnageRate(selectedExpediency.calculatedTonnageRate);
                    setFuelLevyAmount(selectedExpediency.fuelLevyAmount);
                    setTotalCost(selectedExpediency.totalCost);
                }
                setQuote(quoteResponse);
            }
            onQuote(quoteResponse);
        })
            .catch((error) => axiosErrorHandler(error, setErrorMessage))
            .finally(() => setLoading(false));
    };
    const onVehicleTypeSelected = (vehicleType) => {
        const vehicleTypeId = vehicleType.id;
        setVehicleTypeId(vehicleTypeId);
        const pickupReadyAt = form.getFieldValue("pickupReadyAt");
        loadQuote(vehicleTypeId, deliveryExpediencyCode, paymentMethod, offerCode, pickupReadyAt, payAsReturn);
    };
    const onDeliveryExpediencyChange = (elem) => {
        const deliveryExpediencyCode = elem.target.value;
        const selectedExpediency = quote?.deliveryExpediencies.find(quote => quote.deliveryExpediency.code === deliveryExpediencyCode);
        if (selectedExpediency) {
            setDeliveryExpediencyCode(deliveryExpediencyCode);
            setServiceAndProductCostToTenant(selectedExpediency.serviceAndProductCostToTenant);
            setCalculatedTonnageRate(selectedExpediency.calculatedTonnageRate);
            setFuelLevyAmount(selectedExpediency.fuelLevyAmount);
            setTotalCost(selectedExpediency.totalCost);
        }
    };
    const disableBeforeToday = (current) => {
        return current && current < dayjs();
    };
    const initialPickupReadyAt = () => {
        const now = dayjs();
        const min = now.minute();
        if (min % 15 !== 0) {
            now.minute(Math.ceil(min / 15) * 15);
        }
        const deliveryFrom = dayjs(consignment.deliveryFrom);
        return deliveryFrom.isBefore(now) ? now : deliveryFrom;
    };
    const onPickupReadyAtChanged = (pickupReadyAt /*, dateString*/) => {
        loadQuote(vehicleTypeId, deliveryExpediencyCode, paymentMethod, offerCode, pickupReadyAt, payAsReturn);
    };
    const checkHasAnyCustomerDiscountAmount = () => {
        if (quote?.deliveryExpediencies.length) {
            const firstDiscount = quote.deliveryExpediencies.find(it => !!it.customerDiscountAmount);
            return !!firstDiscount;
        }
        return false;
    };
    const marketplaceDistanceMetres = quote?.marketplaceDistanceMetres || marketplaceListing?.distanceMetres;
    const marketplaceTravelSeconds = quote?.marketplaceTravelSeconds || marketplaceListing?.travelSeconds;
    const effectiveTonnes = quote?.effectiveTonnes || marketplaceListing?.effectiveTonnes || 0;
    const preferredVehicleType = quote && quote.preferredVehicleType;
    const hasAnyCustomerDiscountAmount = checkHasAnyCustomerDiscountAmount();
    const consignmentId = consignment.id;
    const consignmentStatus = consignment.status;
    return (_jsxs(Spin, { spinning: loading, children: [_jsxs(Form, { form: form, layout: "vertical", children: [_jsxs(Row, { gutter: [16, 16], className: "no-margin", children: [_jsx(Col, { xs: 24, children: _jsx(VehicleTypeImgPicker, { vehicleType: preferredVehicleType, tenantId: consignment.tenantId, performsCustomerDeliveries: true, marketplaceParticipator: true, onChange: onVehicleTypeSelected }) }), _jsx(Col, { xs: 24, lg: 12, children: quote && quote.deliveryExpediencies &&
                                    _jsx(Form.Item, { name: "deliveryExpediency", label: _jsx(HelpLabel, { title: "Delivery Expediency (ex GST)", size: "large", helpText: _jsxs("div", { children: [_jsx("p", { children: "Delivery Expediency" }), _jsxs("ul", { children: [_jsxs("li", { children: [_jsx("strong", { children: "2Hr Express Delivery" }), " - Available 12am to 10pm for delivery before 12am (midnight)"] }), _jsxs("li", { children: [_jsx("strong", { children: "4Hr Delivery" }), " - Available 12am to 8pm for delivery before 12am (midnight)"] }), _jsxs("li", { children: [_jsx("strong", { children: "Same Day Delivery" }), " - Posted by 12pm for delivery by 5pm. Posted by 2pm for delivery by 7pm."] })] })] }) }), rules: [{
                                                required: true,
                                                message: 'Please select a delivery expediency.',
                                            }], children: _jsx(Radio.Group, { children: _jsx(Space, { orientation: "vertical", children: quote.deliveryExpediencies.map(quote => {
                                                    return (_jsxs(Radio, { onChange: onDeliveryExpediencyChange, value: quote.deliveryExpediency.code, children: [_jsx("strong", { children: quote.deliveryExpediency.description }), " - ", _jsx(CurrencyFormat, { value: quote.totalCost }), "\u00A0", quote.customerDiscountAmount > 0 &&
                                                                _jsxs(Tag, { color: "green", children: [_jsx(CurrencyFormat, { value: quote.customerDiscountAmount }), " Discount"] })] }, quote.deliveryExpediency.code));
                                                }) }) }) }) }), _jsxs(Col, { xs: 24, lg: 12, children: [_jsx(Form.Item, { name: "paymentMethod", label: "Payment Method", initialValue: paymentMethod, className: classnames({ 'display-none': !hasTonnes }), rules: [{
                                                required: hasTonnes,
                                                message: 'Please select a payment method.',
                                            }], children: _jsx(Radio.Group, { onChange: onPaymentMethodChanged, children: MarketplaceConsignmentPaymentMethod.values.map(t => {
                                                return (_jsx("div", { style: { padding: '0 0 10px 0' }, children: _jsxs(Radio, { value: t.key, children: [t.fullDescription, _jsx(Popover, { content: t.help(), children: _jsx(QuestionCircleOutlined, { style: { fontSize: '16pt', marginLeft: '20px' } }) })] }, t.key) }, t.key));
                                            }) }) }), _jsx(Form.Item, { name: "payAsReturn", label: "", valuePropName: 'checked', children: _jsx(Checkbox, { onChange: onPayAsReturnChanged, children: _jsx(HelpLabel, { title: "Pay Driver to Return", helpText: "Select this to pay the driver to return to the pickup location.  This is useful if you need the driver to pickup something from the destination and return it to you." }) }) })] })] }), _jsxs(Row, { gutter: 10, className: "no-margin", children: [_jsx(Col, { xs: 24, lg: 12, children: mode == CalculateMode.POST_IN_MARKET &&
                                    _jsx(Form.Item, { name: "pickupReadyAt", label: "Pick up ready at", initialValue: marketplaceListing ? null : initialPickupReadyAt(), rules: [{
                                                required: !marketplaceListing,
                                                message: 'Please enter the date and time at which the freight is ready for pick up.',
                                            }], children: _jsx(DatePicker, { showTime: true, showNow: false, minuteStep: 15, presets: [
                                                { label: 'In 1 Hour', value: dayjs().add(1, 'hours').minute(Math.ceil(dayjs().minute() / 15) * 15) },
                                                { label: 'In 2 Hours', value: dayjs().add(2, 'hours').minute(Math.ceil(dayjs().minute() / 15) * 15) },
                                                { label: 'In 4 Hours', value: dayjs().add(4, 'hours').minute(Math.ceil(dayjs().minute() / 15) * 15) },
                                                { label: 'Tomorrow 8am', value: dayjs().add(1, 'days').hour(8).minute(0) }
                                            ], onChange: onPickupReadyAtChanged, disabledDate: disableBeforeToday, format: "DD/MM/YYYY HH:mm", placeholder: "Pick up ready at" }) }) }), _jsx(Col, { xs: 24, lg: 12, children: mode == CalculateMode.POST_IN_MARKET &&
                                    _jsxs(_Fragment, { children: [_jsx(Form.Item, { name: "nominatedCost", label: _jsx(HelpLabel, { title: (_jsxs(_Fragment, { children: ["Nominated Payment Amount\u00A0", _jsx("small", { children: "(ex GST)" })] })), helpText: "Urgent job?  You can pay extra to encourage a driver to accept the request sooner" }), help: "Must exceed the sub total", className: classnames({ 'display-none': paymentMethod !== MarketplaceConsignmentPaymentMethod.DELIVERY }), children: _jsx(CurrencyInput, { min: 0, step: 1, onChange: debouncedRefreshQuote, style: { width: "120px" }, disabled: !deliveryExpediencyCode }) }), _jsx(Form.Item, { name: "nominatedTonnageRate", label: _jsx(HelpLabel, { title: _jsxs(_Fragment, { children: ["Nominate a Tonnage Rate ", _jsx("small", { children: "(ex GST)" })] }), helpText: "Urgent job?  You can pay extra to encourage a driver to accept the request sooner" }), help: "Must exceed the below tonnage rate", className: classnames({ 'display-none': paymentMethod !== MarketplaceConsignmentPaymentMethod.TONNE }), children: _jsx(CurrencyInput, { min: 0, onChange: debouncedRefreshQuote, style: { width: "120px" }, disabled: !deliveryExpediencyCode }) })] }) })] }), _jsxs(Row, { gutter: 10, className: "no-margin", children: [_jsxs(Col, { xs: 12, children: [_jsxs("dl", { children: [_jsxs("dt", { children: ["Sub Total ", _jsx("small", { children: "(ex GST)" })] }), _jsx("dd", { children: serviceAndProductCostToTenant ? _jsx(CurrencyFormat, { value: serviceAndProductCostToTenant }) : _jsx(_Fragment, { children: "\u2014" }) })] }), !!fuelLevyAmount &&
                                        _jsxs("dl", { children: [_jsxs("dt", { children: ["Fuel Levy ", _jsx("small", { children: "(ex GST)" })] }), _jsx("dd", { children: _jsx(CurrencyFormat, { value: fuelLevyAmount }) })] }), paymentMethod === MarketplaceConsignmentPaymentMethod.TONNE &&
                                        _jsxs("dl", { children: [_jsxs("dt", { children: ["Tonnage Rate ", _jsx("small", { children: "(ex GST)" })] }), _jsx("dd", { children: calculatedTonnageRate ? _jsx(CurrencyFormat, { value: calculatedTonnageRate }) : _jsx(_Fragment, { children: "\u2014" }) })] }), _jsxs("dl", { children: [_jsxs("dt", { children: [paymentMethod === MarketplaceConsignmentPaymentMethod.TONNE ? "Est. " : "", "Total Cost ", _jsx("small", { children: "(ex GST)" })] }), _jsx("dd", { children: totalCost ? _jsx(CurrencyFormat, { value: totalCost }) : _jsx(_Fragment, { children: "\u2014" }) })] })] }), _jsxs(Col, { xs: 12, children: [_jsxs("dl", { children: [_jsx("dt", { children: "Distance (One Way)" }), _jsx("dd", { children: marketplaceDistanceMetres ? formatMetres(marketplaceDistanceMetres, false) : _jsx(_Fragment, { children: "\u2014" }) })] }), _jsxs("dl", { children: [_jsx("dt", { children: "Travel Time (One Way)" }), _jsx("dd", { children: marketplaceTravelSeconds ? formatSecondsAsDuration(marketplaceTravelSeconds) : _jsx(_Fragment, { children: "\u2014" }) })] }), paymentMethod === MarketplaceConsignmentPaymentMethod.TONNE &&
                                        _jsxs("dl", { children: [_jsx("dt", { children: "Tonnes" }), _jsx("dd", { children: effectiveTonnes ? effectiveTonnes : _jsx(_Fragment, { children: "\u2014" }) })] })] })] }), mode == CalculateMode.POST_IN_MARKET &&
                        _jsx(Row, { gutter: 10, className: "no-margin", children: _jsx(Col, { xs: 24, children: _jsx(Form.Item, { name: "offerCode", label: "Discount Code", help: "If you have a discount code enter it here to get a discount on your delivery", hasFeedback: hasAnyCustomerDiscountAmount, validateStatus: hasAnyCustomerDiscountAmount ? "success" : "", children: _jsx(Input.Search, { enterButton: hasAnyCustomerDiscountAmount ? "Remove" : "Apply", onSearch: onValidateOrRemoveDiscountCode }) }) }) })] }), errorMessage &&
                _jsx(Alert, { type: "error", title: errorMessage, banner: true }), _jsx(ConsignmentHasBeenInvoicedAlert, { consignmentId: consignmentId, consignmentStatus: consignmentStatus })] }));
};
const CalculateAmountFormForwardRef = React.forwardRef(CalculateAmountForm);
const ConsignmentMarketplaceModal = ({ open, consignment, marketplaceListing, mode = CalculateMode.POST_IN_MARKET, onClose, onUpdated, onCancel }) => {
    const [saving, setSaving] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [quote, setQuote] = useState(undefined);
    const [deliverMarketplaceListing, setDeliverMarketplaceListing] = useState(marketplaceListing);
    const [consignmentMarketplaceRequest, setConsignmentMarketplaceRequest] = useState(undefined);
    const calcFormRef = useRef(null);
    const paymentFormRef = useRef(null);
    const consignmentId = consignment?.id;
    useEffect(() => {
        if (open && consignment) {
            setErrorMessage(undefined);
            setShowPaymentForm(false);
            setQuote(undefined);
            calcFormRef.current?.resetFields();
            const consignmentResponse = consignment;
            if (!marketplaceListing && consignmentResponse.marketplaceListings?.length > 0) {
                setDeliverMarketplaceListing(consignmentResponse.marketplaceListings && consignmentResponse.marketplaceListings.length
                    ? consignmentResponse.marketplaceListings.find(it => it.consignmentAction === 'DELIVER')
                    : undefined);
            }
            else if (marketplaceListing) {
                setDeliverMarketplaceListing(marketplaceListing);
            }
        }
    }, [open, consignment, marketplaceListing]);
    const onOk = () => {
        if (showPaymentForm) {
            paymentFormRef.current?.submit();
        }
        else {
            validateFieldsAndUpdate();
        }
    };
    const validateFieldsAndUpdate = () => {
        calcFormRef.current?.validateFields().then((formValues) => {
            if (hasValidQuote()) {
                setSaving(true);
                setErrorMessage(undefined);
                doPostOrPatchOnMarketplace(formValues, undefined);
            }
        })
            .catch(errorInfo => console.error(errorInfo)); // This has to be present otherwise antd crashes :(
    };
    const hasValidQuote = () => {
        return !!quote?.preferredVehicleType.id;
    };
    const doPostOrPatchOnMarketplace = (formValues, stripeSourceId) => {
        const request = {
            preferredVehicleTypeId: quote?.preferredVehicleType.id,
            deliveryExpediency: formValues.deliveryExpediency,
            nominatedCostByTenant: formValues.nominatedCost,
            pickupReadyAt: formValues.pickupReadyAt,
            marketplaceListingVersion: marketplaceListing?.version || undefined,
            stripeSourceId: stripeSourceId,
            paymentMethod: formValues.paymentMethod,
            nominatedTonnageRate: formValues.nominatedTonnageRate,
            offerCode: formValues.offerCode,
            payAsReturn: formValues.payAsReturn
        };
        setConsignmentMarketplaceRequest(request);
        if (marketplaceListing) {
            axios.put(`/admin/v1/consignments/${consignmentId}/marketplace`, request)
                .then(onAxiosCallSuccess)
                .catch(onAxiosCallError);
        }
        else {
            axios.post(`/admin/v1/consignments/${consignmentId}/marketplace`, request)
                .then(onAxiosCallSuccess)
                .catch(onAxiosCallError);
        }
    };
    const onAxiosCallSuccess = ( /*response: AxiosResponse*/) => {
        onUpdated && onUpdated();
        setSaving(false);
    };
    const onAxiosCallError = (error) => {
        setSaving(false);
        if (isConcurrentModificationError(error)) {
            setErrorMessage("The delivery could not be place on marketplace. This is because another user has updated the same delivery.  Please refresh the page and try again.");
        }
        else {
            if (isPaymentRequiredError(error)) {
                setShowPaymentForm(true);
            }
            else {
                axiosErrorHandler(error, setErrorMessage);
            }
        }
    };
    const onPurchaseComplete = () => {
        onUpdated && onUpdated();
        setSaving(false);
    };
    const onCreatePaymentIntent = () => {
        return axios.post(`/admin/v1/consignments/${consignmentId}/marketplace/payment-intent`, consignmentMarketplaceRequest);
    };
    let title = "Marketplace Contractor Rate";
    if (mode == CalculateMode.POST_IN_MARKET) {
        title = "Place Delivery on Marketplace";
    }
    if (mode == CalculateMode.QUOTE) {
        title = "Marketplace Quote";
    }
    const modalWidth = window.innerWidth > 800 ? 800 : 520;
    return (_jsxs(Modal, { title: title, open: open, onOk: onOk, onCancel: onCancel, confirmLoading: saving, destroyOnHidden: true, width: modalWidth, footer: mode == CalculateMode.QUOTE ? [
            _jsx(Button, { onClick: onClose, children: "Close" }, "cancel"),
        ] : undefined, children: [consignment && !showPaymentForm &&
                _jsx(CalculateAmountFormForwardRef, { consignment: consignment, marketplaceListing: deliverMarketplaceListing, mode: mode, onQuote: setQuote, ref: calcFormRef }), open && showPaymentForm && consignmentId &&
                _jsx(StripePaymentIntentForm, { ref: paymentFormRef, onCreatePaymentIntent: onCreatePaymentIntent, returnUrl: `/#/app/consignments/view/${consignmentId}?${PARAM_SHOW_PLACE_ON_MARKET_MODAL}=true`, onPaymentSuccess: onPurchaseComplete }), errorMessage && _jsx(Alert, { type: "error", title: errorMessage, banner: true })] }));
};
export default ConsignmentMarketplaceModal;
